import { useAppDispatch, useAppSelector } from "../../app/hooks";
import * as changeCase from "change-case";

import {
  WeightUnitType,
  calculateTapped,
  inputTypeOnChanged,
  inputValueOnChanged,
  outputTypeOnChanged,
  resultCopyTapped,
  selectState,
} from "./WeightUnitConverterSlice";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { ResultCard } from "../../components/result_card/ResultCard";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { EqualColumnTable } from "../../components/equal_column_table/EqualColumnTable";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { HomeCard } from "../../components/home_card/HomeCard";
import TemperatureConverterImage from "../../assets/TemperatureConverter.webp";
import DistanceUnitConverterImage from "../../assets/DistanceUnitConverter.webp";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { useTranslation } from "react-i18next";

export function WeightUnitConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("weightConverter.title")}
      pageSeoTitle={t("weightConverter.seoTitle")}
      pageDescription={t("weightConverter.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("weightConverter.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("weightConverter.fromLabel")}
            value={state.inputUnit}
            onChange={(e) =>
              dispatch(
                inputTypeOnChanged(Number(e.target.value) as WeightUnitType)
              )
            }
          >
            <MenuItem value={WeightUnitType.centigram}>{t("weightConverter.options.centigram")}</MenuItem>
            <MenuItem value={WeightUnitType.decigram}>{t("weightConverter.options.decigram")}</MenuItem>
            <MenuItem value={WeightUnitType.dekagram}>{t("weightConverter.options.dekagram")}</MenuItem>
            <MenuItem value={WeightUnitType.dram}>{t("weightConverter.options.dram")}</MenuItem>
            <MenuItem value={WeightUnitType.grain}>{t("weightConverter.options.grain")}</MenuItem>
            <MenuItem value={WeightUnitType.gram}>{t("weightConverter.options.gram")}</MenuItem>
            <MenuItem value={WeightUnitType.hectogram}>{t("weightConverter.options.hectogram")}</MenuItem>
            <MenuItem value={WeightUnitType.hundredWeightLongUk}>
              {t("weightConverter.options.hundredWeightLongUk")}
            </MenuItem>
            <MenuItem value={WeightUnitType.hunderdWeightShortUs}>
              {t("weightConverter.options.hunderdWeightShortUs")}
            </MenuItem>
            <MenuItem value={WeightUnitType.kilogram}>{t("weightConverter.options.kilogram")}</MenuItem>
            <MenuItem value={WeightUnitType.lbLbs}>{t("weightConverter.options.lbLbs")}</MenuItem>
            <MenuItem value={WeightUnitType.longTon}>{t("weightConverter.options.longTon")}</MenuItem>
            <MenuItem value={WeightUnitType.megagram}>{t("weightConverter.options.megagram")}</MenuItem>
            <MenuItem value={WeightUnitType.metricTon}>{t("weightConverter.options.metricTon")}</MenuItem>
            <MenuItem value={WeightUnitType.microgram}>{t("weightConverter.options.microgram")}</MenuItem>
            <MenuItem value={WeightUnitType.milligram}>{t("weightConverter.options.milligram")}</MenuItem>
            <MenuItem value={WeightUnitType.ounce}>{t("weightConverter.options.ounce")}</MenuItem>
            <MenuItem value={WeightUnitType.pound}>{t("weightConverter.options.pound")}</MenuItem>
            <MenuItem value={WeightUnitType.shortTon}>{t("weightConverter.options.shortTon")}</MenuItem>
            <MenuItem value={WeightUnitType.tonLongUk}>{t("weightConverter.options.tonLongUk")}</MenuItem>
            <MenuItem value={WeightUnitType.tonMetric}>{t("weightConverter.options.tonMetric")}</MenuItem>
            <MenuItem value={WeightUnitType.tonShortUs}>
              {t("weightConverter.options.tonShortUs")}
            </MenuItem>
            <MenuItem value={WeightUnitType.tonne}>{t("weightConverter.options.tonne")}</MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("weightConverter.toLabel")}
            value={state.outputUnit}
            onChange={(e) =>
              dispatch(
                outputTypeOnChanged(Number(e.target.value) as WeightUnitType)
              )
            }
          >
            <MenuItem value={WeightUnitType.centigram}>{t("weightConverter.options.centigram")}</MenuItem>
            <MenuItem value={WeightUnitType.decigram}>{t("weightConverter.options.decigram")}</MenuItem>
            <MenuItem value={WeightUnitType.dekagram}>{t("weightConverter.options.dekagram")}</MenuItem>
            <MenuItem value={WeightUnitType.dram}>{t("weightConverter.options.dram")}</MenuItem>
            <MenuItem value={WeightUnitType.grain}>{t("weightConverter.options.grain")}</MenuItem>
            <MenuItem value={WeightUnitType.gram}>{t("weightConverter.options.gram")}</MenuItem>
            <MenuItem value={WeightUnitType.hectogram}>{t("weightConverter.options.hectogram")}</MenuItem>
            <MenuItem value={WeightUnitType.hundredWeightLongUk}>
              {t("weightConverter.options.hundredWeightLongUk")}
            </MenuItem>
            <MenuItem value={WeightUnitType.hunderdWeightShortUs}>
              {t("weightConverter.options.hunderdWeightShortUs")}
            </MenuItem>
            <MenuItem value={WeightUnitType.kilogram}>{t("weightConverter.options.kilogram")}</MenuItem>
            <MenuItem value={WeightUnitType.lbLbs}>{t("weightConverter.options.lbLbs")}</MenuItem>
            <MenuItem value={WeightUnitType.longTon}>{t("weightConverter.options.longTon")}</MenuItem>
            <MenuItem value={WeightUnitType.megagram}>{t("weightConverter.options.megagram")}</MenuItem>
            <MenuItem value={WeightUnitType.metricTon}>{t("weightConverter.options.metricTon")}</MenuItem>
            <MenuItem value={WeightUnitType.microgram}>{t("weightConverter.options.microgram")}</MenuItem>
            <MenuItem value={WeightUnitType.milligram}>{t("weightConverter.options.milligram")}</MenuItem>
            <MenuItem value={WeightUnitType.ounce}>{t("weightConverter.options.ounce")}</MenuItem>
            <MenuItem value={WeightUnitType.pound}>{t("weightConverter.options.pound")}</MenuItem>
            <MenuItem value={WeightUnitType.shortTon}>{t("weightConverter.options.shortTon")}</MenuItem>
            <MenuItem value={WeightUnitType.tonLongUk}>{t("weightConverter.options.tonLongUk")}</MenuItem>
            <MenuItem value={WeightUnitType.tonMetric}>{t("weightConverter.options.tonMetric")}</MenuItem>
            <MenuItem value={WeightUnitType.tonShortUs}>
              {t("weightConverter.options.tonShortUs")}
            </MenuItem>
            <MenuItem value={WeightUnitType.tonne}>{t("weightConverter.options.tonne")}</MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("weightConverter.valueLabel")}
            variant="outlined"
            color="info"
            value={state.inputValue || ""}
            onChange={(e) =>
              dispatch(inputValueOnChanged(Number(e.target.value)))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{state.inputSymbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 1, mb: 3 }}>
          <CustomButton
            label={t("weightConverter.convertButton")}
            onClick={() => {
              dispatch(calculateTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.outputValue}`}
            symbol={`${state.resultSymbol}`}
            description={changeCase.pascalCase(
              WeightUnitType[state.resultUnit]
            )}
            onCopy={() => dispatch(resultCopyTapped())}
            resultCopied={state.resultCopied}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("weightConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("weightConverter.content.freeDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("weightConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("weightConverter.content.howToDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("weightConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="distanceConverter"
            link="/DistanceUnitConverter"
            smallCard={true}
            image={DistanceUnitConverterImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="temperatureConverter"
            link="/TemperatureConverter"
            smallCard={true}
            image={TemperatureConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
